import { askConfirmation } from '@/ui/ConfirmContext';
import { countQueuedWork, type OutboxStore } from './outbox';

/** What the guard may be given instead of the real device. */
export interface SignOutGuardOptions {
  /** Where the queue is kept; defaults to the device's IndexedDB queue. */
  readonly store?: OutboxStore;
  /** Asks the person to confirm; defaults to the app's confirmation dialog. */
  readonly confirm?: (message: string) => boolean | Promise<boolean>;
}

/**
 * The words the sign-out warning uses.
 *
 * The question itself is the dialog's title and its button, so this is only what is at stake.
 *
 * @param count how many changes are still waiting
 * @returns what signing out now would leave behind
 */
export function queuedWorkWarning(count: number): string {
  const changes = count === 1 ? '1 field change has' : `${String(count)} field changes have`;
  return `${changes} not been sent yet. They stay on this device and can only be sent once you sign in here again.`;
}

function askInDialog(message: string): Promise<boolean> {
  return askConfirmation({
    tier: 'destructive',
    title: 'Sign out with unsent field changes?',
    consequence: message,
    confirmLabel: 'Sign out anyway',
    cancelLabel: 'Stay signed in',
  });
}

/**
 * Asks before a sign-out that would strand queued field work.
 *
 * Wire this in front of every sign-out control: `if (await confirmSignOut(identity)) void signOut();`.
 * The queue is keyed to the person who recorded it, so work left behind is not lost, but nobody
 * else can send it either.
 *
 * The question is asked in the app's confirmation dialog, so a `ConfirmProvider` must be mounted
 * whenever there is queued work; without one the returned promise rejects rather than signing the
 * person out unasked.
 *
 * @param identity the signed-in session identity
 * @param options a store and a confirm function, for tests
 * @returns true when there is nothing waiting or the person chose to sign out anyway
 */
export async function confirmSignOut(
  identity: string | null,
  options: SignOutGuardOptions = {},
): Promise<boolean> {
  const count = await countQueuedWork(identity, options.store);
  if (count === 0) {
    return true;
  }
  const ask = options.confirm ?? askInDialog;
  return ask(queuedWorkWarning(count));
}
